import { Injectable } from '@angular/core';
import { CategoricalTraitInfo, CategoricalValue, CharacterTraitInfo, NumericTraitInfo, TraitCondition, TraitFilter, TraitInfo } from '../trait.reference.model';

@Injectable()
export class TraitFilterContext {
    categoricalTraits: CategoricalTraitInfo[] = [];
    characterTraits: CharacterTraitInfo[] = [];
    numericTraits: NumericTraitInfo[] = [];
    protected readonly TraitCondition = TraitCondition;

    initialize(): void {
        this.numericTraits = [];
        this.characterTraits = [];
        this.categoricalTraits = [];
    }

    areAllTraitsDropped(): boolean {
        const allNumericDropped = this.numericTraits
            .every(t => t.condition === TraitCondition.DROP_TRAIT);

        const allCharacterDropped = this.characterTraits
            .every(t => t.condition === TraitCondition.DROP_TRAIT);

        const allCategoricalDropped = this.categoricalTraits
            .every(t => t.condition === TraitCondition.DROP_TRAIT);

        return allNumericDropped && allCharacterDropped && allCategoricalDropped;
    }

    areNumericInvalidLimits(): boolean {
        return !this.numericTraits.every((numeric) => this.isNumericLimitValid(numeric));
    }

    isNumericLimitValid(numberTrait: NumericTraitInfo): boolean {
        const condition = numberTrait.condition;

        if (condition === TraitCondition.KEEP_ALL || condition === TraitCondition.DROP_TRAIT) {
            numberTrait.limits = '';
            return true;
        }

        const value = numberTrait.limits?.trim();
        if (!value) {
            return false;
        }

        switch (condition) {
        case TraitCondition.IN:
        case TraitCondition.NOT_IN:
            return this.isValidNumberArray(numberTrait, value);
        case TraitCondition.BETWEEN:
            return this.isValidRange(numberTrait, value);
        default:
            if (!this.isValidNumber(value)) {
                return false;
            }
            return this.isValidLowerLimits(numberTrait.minValue, value) && this.isValidUpperLimits(numberTrait.maxValue, value);
        }
    }

    isCategoricalLimitValid(categoricalTrait: CategoricalTraitInfo): boolean {
        const condition = categoricalTrait.condition;

        if (condition === TraitCondition.KEEP_ALL || condition === TraitCondition.DROP_TRAIT) {
            categoricalTrait.limits = '';
            return true;
        }

        const value = categoricalTrait.limits?.trim();
        if (!value) {
            return false;
        }

        if (condition === TraitCondition.IN || condition === TraitCondition.NOT_IN) {
            return this.isValidCategoricalArray(categoricalTrait, value);
        }

        return false;
    }

    isValidCategoricalArray(categoricalTrait: CategoricalTraitInfo, value: string): boolean {
        if (value == null || value.trim() === '') {
            return false;
        }
        const parts = value.split(',').map(part => part.trim());
        return parts.every(part => this.existsInValues(categoricalTrait.values, part));
    }

    existsInValues(values: CategoricalValue[], inputValue: string): boolean {
        return values.some(val => val.name === inputValue);
    }

    isValidNumber(value: string): boolean {
        if (value == null || value.trim() === '') {
            return false;
        }
        return !isNaN(Number(value));
    }

    isValidNumberArray(numberTrait: NumericTraitInfo, value: string): boolean {
        if (value == null || value.trim() === '') {
            return false;
        }
        const parts = value.split(',').map(part => part.trim());
        return parts.every(part => this.isValidNumber(part) && this.isValidLowerLimits(numberTrait.minValue, part) && this.isValidUpperLimits(numberTrait.maxValue, part));
    }

    isValidRange(numberTrait: NumericTraitInfo, value: string): boolean {
        if (value == null || value.trim() === '') {
            return false;
        }
        const parts = value.split('-').map(part => part.trim());
        if (parts.length !== 2) {
            return false;
        }

        return parts.every(part => this.isValidNumber(part) && this.isValidLowerLimits(numberTrait.minValue, part) && this.isValidUpperLimits(numberTrait.maxValue, part));
    }

    isValidLowerLimits(lowerValue: number, value: string) {
        return Number(value) >= lowerValue;
    }

    isValidUpperLimits(upperValue: number, value: string) {
        return Number(value) <= upperValue;
    }

    hasNumericTraits(): boolean {
        return this.numericTraits != null && this.numericTraits.length > 0;
    }

    hasCharacterTraits(): boolean {
        return this.characterTraits != null && this.characterTraits.length > 0;
    }

    hasCategoricalTraits(): boolean {
        return this.categoricalTraits != null && this.categoricalTraits.length > 0;
    }

    getNumericTraitFilters() {
        const numericFilters: TraitFilter[] = [];

        this.numericTraits.filter(numericTrait => numericTrait.condition !== TraitCondition.DROP_TRAIT).forEach(numericTrait => {
            const numLimits = this.buildNumericLimits(numericTrait);
            numericFilters.push({
                traitInfo: new TraitInfo(numericTrait.id, numericTrait.name, numericTrait.locationCount, numericTrait.germplasmCount, numericTrait.observationCount, null, numericTrait.limits, numericTrait.priority),
                condition: numericTrait.condition,
                limits: numLimits,
                priority: numericTrait.priority
            });
        });
        return numericFilters;
    }

    getCharacterTraitFilters() {
        const characterFilters: TraitFilter[] = [];
        this.characterTraits.filter(characterTrait => characterTrait.condition !== TraitCondition.DROP_TRAIT).forEach(characterTrait => {
            const characterLimits = this.parseStringList(characterTrait.limits);
            characterFilters.push({
                traitInfo: new TraitInfo(characterTrait.id, characterTrait.name, characterTrait.locationCount, characterTrait.germplasmCount, characterTrait.observationCount, null, characterTrait.limits, characterTrait.priority),
                condition: characterTrait.condition,
                limits: characterLimits,
                priority: characterTrait.priority
            });
        });

        return characterFilters;
    }

    getCategoricalTraitFilters() {
        const categoricalFilters: TraitFilter[] = [];
        this.categoricalTraits.filter(categoricalTrait => categoricalTrait.condition !== TraitCondition.DROP_TRAIT).forEach(categoricalTrait => {
            const categoricalLimits = this.parseStringList(categoricalTrait.limits);
            categoricalFilters.push({
                traitInfo: new TraitInfo(categoricalTrait.id, categoricalTrait.name, categoricalTrait.locationCount, categoricalTrait.germplasmCount, categoricalTrait.observationCount, null, categoricalTrait.limits, categoricalTrait.priority),
                condition: categoricalTrait.condition,
                limits: categoricalLimits,
                priority: categoricalTrait.priority
            });
        });

        return categoricalFilters;
    }

    buildNumericLimits(numericFilter): number[] {
        switch (numericFilter.condition) {
        case TraitCondition.BETWEEN:
            return this.parseRange(numericFilter.limits);
        case TraitCondition.NOT_IN:
        case TraitCondition.IN:
            return this.parseNumberList(numericFilter.limits);
        case TraitCondition.EQUAL:
        case TraitCondition.GREATER_THAN:
        case TraitCondition.GREATER_THAN_EQUAL:
        case TraitCondition.LESS_THAN:
        case TraitCondition.LESS_THAN_EQUAL:
            return [this.parseNumber(numericFilter.limits)];
        default:
            return [];
        }
    }

    private parseNumber(value: string): number | null {
        const num = Number(value);
        return isNaN(num) ? null : num;
    }

    private parseNumberList(value: string): number[] | null {
        if (!value) {
            return null;
        }
        const parts = value.split(',').map(p => p.trim()).filter(p => p !== '');
        const numbers = parts.map(this.parseNumber);
        if (numbers.some(n => n === null)) {
            return null;
        }
        return numbers as number[];
    }

    private parseStringList(value: string): string[] | null {
        if (!value) {
            return null;
        }
        const parts = value.split(',').map(p => p.trim())
            .filter(p => p !== '');
        return parts.length > 0 ? parts : null;
    }

    private parseRange(value: string): [number, number] | null {
        if (!value) {
            return null;
        }
        const parts = value.split('-').map(p => p.trim());
        if (parts.length !== 2) {
            return null;
        }

        const start = this.parseNumber(parts[0]);
        const end = this.parseNumber(parts[1]);

        if (start === null || end === null) {
            return null;
        }
        if (start > end) {
            return null;
        }
        return [start, end];
    }
}